import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, Lock, Unlock, UtensilsCrossed, Wine, Plus, AlertTriangle, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export default function AdminOpeningStock() {
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New entry form
  const [showNew, setShowNew] = useState(false);
  const [newDate, setNewDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [newKitchenValue, setNewKitchenValue] = useState('');
  const [newBarValue, setNewBarValue] = useState('');

  useEffect(() => {
    (async () => {
      const res = await axios.get(`${API}/outlets`, { headers: getHeaders() });
      setOutlets(res.data);
      if (res.data.length > 0) setSelectedOutlet(res.data[0].id);
    })();
  }, [API, getHeaders]);

  const load = useCallback(async () => {
    if (!selectedOutlet) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inventory/opening?outlet_id=${selectedOutlet}`, { headers: getHeaders() });
      setEntries(res.data);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, [selectedOutlet, API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (cg, lock = false) => {
    const val = cg === 'kitchen' ? parseFloat(newKitchenValue) || 0 : parseFloat(newBarValue) || 0;
    if (!newDate) { toast.error('Select an applicable from date'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/inventory/opening`, {
        outlet_id: selectedOutlet, category_group: cg, value: val, applicable_from: newDate, lock
      }, { headers: getHeaders() });
      toast.success(`${cg === 'kitchen' ? 'Kitchen' : 'Bar'} opening ${lock ? 'saved & locked' : 'saved'} from ${newDate}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleUnlock = async (applicableFrom, cg) => {
    try {
      await axios.post(`${API}/admin/inventory/opening/unlock`, {
        outlet_id: selectedOutlet, applicable_from: applicableFrom, category_group: cg
      }, { headers: getHeaders() });
      toast.success(`${cg} opening unlocked`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const outletName = outlets.find(o => o.id === selectedOutlet)?.name || '';

  // Group entries by applicable_from date
  const grouped = {};
  entries.forEach(e => {
    const key = e.applicable_from || `legacy-${e.year}-${e.month}`;
    if (!grouped[key]) grouped[key] = {};
    grouped[key][e.category_group] = e;
  });
  const sortedDates = Object.keys(grouped).sort().reverse();

  return (
    <div className="space-y-4" data-testid="admin-opening-stock-page">
      <div>
        <h1 className="text-xl font-bold text-[#1A2421]">Opening Stock</h1>
        <p className="text-sm text-[#5C6B64] mt-0.5">Set inventory baseline values with applicable dates</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
          <SelectTrigger className="w-[200px] bg-white border-[#E2E8DF]" data-testid="outlet-select">
            <SelectValue placeholder="Outlet" />
          </SelectTrigger>
          <SelectContent>
            {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {outletName && <Badge className="bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20 text-xs">{outletName}</Badge>}
        <Button variant="outline" size="sm" onClick={() => setShowNew(!showNew)}
          className="ml-auto border-[color:var(--color-primary)] text-[color:var(--color-primary)] text-xs" data-testid="add-opening-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Opening
        </Button>
      </div>

      {/* New Opening Entry Form */}
      {showNew && (
        <Card className="border-[color:var(--color-primary)]/30 bg-[color:var(--color-primary)]/5 shadow-sm" data-testid="new-opening-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[color:var(--color-primary)]" /> New Opening Entry
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="reset-warning">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-700">This will reset inventory calculation from the selected date. Previous closing values will be ignored for this date.</p>
            </div>

            <div>
              <Label className="text-xs text-[#5C6B64]">Applicable From Date</Label>
              <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                className="h-10 border-[#E2E8DF] mt-1 w-48" data-testid="opening-date-input" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Kitchen */}
              <div className="space-y-2 p-3 bg-white rounded-lg border border-[#E2E8DF]">
                <Label className="text-xs text-[#5C6B64] flex items-center gap-1.5">
                  <UtensilsCrossed className="w-3.5 h-3.5 text-emerald-600" /> Kitchen (Food) Opening
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5C6B64]">₹</span>
                  <Input type="number" step="1" min="0" value={newKitchenValue}
                    onChange={e => setNewKitchenValue(e.target.value)}
                    className="pl-7 h-10 border-[#E2E8DF]" placeholder="0"
                    data-testid="new-kitchen-value" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSave('kitchen', false)} disabled={saving}
                    className="flex-1 text-xs border-[#E2E8DF]" data-testid="save-new-kitchen">
                    <Save className="w-3 h-3 mr-1" /> Save
                  </Button>
                  <Button size="sm" onClick={() => handleSave('kitchen', true)} disabled={saving}
                    className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lock-new-kitchen">
                    <Lock className="w-3 h-3 mr-1" /> Save & Lock
                  </Button>
                </div>
              </div>

              {/* Bar */}
              <div className="space-y-2 p-3 bg-white rounded-lg border border-[#E2E8DF]">
                <Label className="text-xs text-[#5C6B64] flex items-center gap-1.5">
                  <Wine className="w-3.5 h-3.5 text-blue-600" /> Bar (Beverage + Alcohol) Opening
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5C6B64]">₹</span>
                  <Input type="number" step="1" min="0" value={newBarValue}
                    onChange={e => setNewBarValue(e.target.value)}
                    className="pl-7 h-10 border-[#E2E8DF]" placeholder="0"
                    data-testid="new-bar-value" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSave('bar', false)} disabled={saving}
                    className="flex-1 text-xs border-[#E2E8DF]" data-testid="save-new-bar">
                    <Save className="w-3 h-3 mr-1" /> Save
                  </Button>
                  <Button size="sm" onClick={() => handleSave('bar', true)} disabled={saving}
                    className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="lock-new-bar">
                    <Lock className="w-3 h-3 mr-1" /> Save & Lock
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Opening Entries */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      ) : sortedDates.length === 0 ? (
        <p className="text-sm text-[#5C6B64] text-center py-8">No opening entries set</p>
      ) : (
        <div className="space-y-3" data-testid="opening-entries-list">
          {sortedDates.map(dateKey => {
            const group = grouped[dateKey];
            const k = group.kitchen;
            const b = group.bar;
            const displayDate = dateKey.startsWith('legacy-') ? dateKey.replace('legacy-', '') : dateKey;
            return (
              <Card key={dateKey} className="border-[#E2E8DF] shadow-sm" data-testid={`opening-entry-${dateKey}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[color:var(--color-primary)]" />
                      <span className="text-sm font-semibold text-[#1A2421]">From: {displayDate}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Kitchen */}
                    <div className="flex items-center justify-between p-2.5 bg-emerald-50/50 rounded-lg border border-emerald-100">
                      <div>
                        <p className="text-[10px] text-[#5C6B64] flex items-center gap-1">
                          <UtensilsCrossed className="w-3 h-3 text-emerald-600" /> Kitchen
                        </p>
                        <p className="text-sm font-bold text-[#1A2421]">
                          {k ? `₹${Math.round(k.value).toLocaleString('en-IN')}` : <span className="text-[#5C6B64] font-normal text-xs">Not set</span>}
                        </p>
                      </div>
                      {k?.locked ? (
                        <Button variant="ghost" size="sm" onClick={() => handleUnlock(dateKey, 'kitchen')}
                          className="text-amber-600 text-[10px] h-7 px-2">
                          <Unlock className="w-3 h-3 mr-0.5" /> Unlock
                        </Button>
                      ) : k ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[9px] border-0">Open</Badge>
                      ) : null}
                    </div>
                    {/* Bar */}
                    <div className="flex items-center justify-between p-2.5 bg-blue-50/50 rounded-lg border border-blue-100">
                      <div>
                        <p className="text-[10px] text-[#5C6B64] flex items-center gap-1">
                          <Wine className="w-3 h-3 text-blue-600" /> Bar
                        </p>
                        <p className="text-sm font-bold text-[#1A2421]">
                          {b ? `₹${Math.round(b.value).toLocaleString('en-IN')}` : <span className="text-[#5C6B64] font-normal text-xs">Not set</span>}
                        </p>
                      </div>
                      {b?.locked ? (
                        <Button variant="ghost" size="sm" onClick={() => handleUnlock(dateKey, 'bar')}
                          className="text-amber-600 text-[10px] h-7 px-2">
                          <Unlock className="w-3 h-3 mr-0.5" /> Unlock
                        </Button>
                      ) : b ? (
                        <Badge className="bg-blue-100 text-blue-700 text-[9px] border-0">Open</Badge>
                      ) : null}
                    </div>
                  </div>
                  {(k?.updated_by || b?.updated_by) && (
                    <p className="text-[10px] text-[#5C6B64] mt-2">
                      Last updated by {k?.updated_by || b?.updated_by}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
